import React, { useState, useEffect, useCallback } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { API_BASE_URL } from '../lib/constants';
import { redirectAfterLogin } from '../lib/authRedirect';

// Facebook SDK types
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    FB: any;
    fbAsyncInit: () => void;
  }
}

interface SocialLoginProps {
  // eslint-disable-next-line no-unused-vars
  onError?: (_error: string) => void;
}

interface SocialAuthConfig {
  google: {
    client_id?: string;
    enabled: boolean;
  };
  facebook: {
    client_id?: string;
    enabled: boolean;
  };
}

const SocialLogin: React.FC<SocialLoginProps> = ({ onError }) => {
  const [authConfig, setAuthConfig] = useState<SocialAuthConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAuthConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/social/config/`);
      setAuthConfig(response.data);
    } catch (error: unknown) {
      console.error('Failed to fetch social auth config:', error);
      onError?.('Failed to load social login options');
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    fetchAuthConfig();
  }, [fetchAuthConfig]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      // Send the credential to our backend
      const response = await axios.post(`${API_BASE_URL}/api/auth/google/callback/`, {
        credential: credentialResponse.credential,
        redirect_uri: window.location.origin
      });

      // Store JWT tokens
      if (response.data.access) {
        localStorage.setItem('token', response.data.access);
      }
      if (response.data.refresh) {
        localStorage.setItem('refresh', response.data.refresh);
      }

      // Redirect after successful login
      redirectAfterLogin();
    } catch (error: unknown) {
      console.error('Google login failed:', error);
      const errorMessage = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      onError?.(errorMessage || 'Google login failed');
    }
  };

  const handleGoogleError = () => {
    onError?.('Google login was cancelled or failed');
  };

  const handleFacebookLogin = async () => {
    if (!authConfig?.facebook.enabled) {
      onError?.('Facebook login is not available');
      return;
    }

    try {
      // Initialize Facebook SDK
      await loadFacebookSDK();
      
      // Perform Facebook login
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      window.FB.login((response: any) => {
        if (response.authResponse) {
          handleFacebookCallback(response.authResponse.code);
        } else {
          onError?.('Facebook login was cancelled');
        }
      }, { scope: 'email', response_type: 'code' });
    } catch (error: unknown) {
      console.error('Facebook login error:', error);
      onError?.('Facebook login failed');
    }
  };

  const handleFacebookCallback = async (code: string) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/facebook/callback/`, {
        code,
        redirect_uri: window.location.origin
      });

      // Store JWT tokens
      if (response.data.access) {
        localStorage.setItem('token', response.data.access);
      }
      if (response.data.refresh) {
        localStorage.setItem('refresh', response.data.refresh);
      }

      // Redirect after successful login
      redirectAfterLogin();
    } catch (error: unknown) {
      console.error('Facebook login failed:', error);
      const errorMessage = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      onError?.(errorMessage || 'Facebook login failed');
    }
  };

  const loadFacebookSDK = (): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      if (window.FB) {
        resolve();
        return;
      }

      window.fbAsyncInit = function() {
        window.FB.init({
          appId: authConfig?.facebook.client_id,
          cookie: true,
          xfbml: true,
          version: 'v17.0'
        });
        resolve();
      };

      // Load Facebook SDK
      if (!document.getElementById('facebook-jssdk')) {
        const js = document.createElement('script');
        js.id = 'facebook-jssdk';
        js.src = 'https://connect.facebook.net/en_US/sdk.js';
        js.onerror = () => reject(new Error('Failed to load Facebook SDK'));
        document.body.appendChild(js);
      }
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
        Loading social login options...
      </div>
    );
  }

  const hasAnySocialLogin = authConfig?.google.enabled || authConfig?.facebook.enabled;

  if (!hasAnySocialLogin) {
    return null;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        margin: '20px 0',
        color: '#666',
        fontSize: '14px'
      }}>
        <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }}></div>
        <span style={{ padding: '0 16px' }}>OR</span>
        <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }}></div>
      </div>

      {authConfig?.google.enabled && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            useOneTap={false}
            theme="outline"
            size="large"
            text="continue_with"
            shape="rectangular"
          />
        </div>
      )}

      {authConfig?.facebook.enabled && (
        <button
          onClick={handleFacebookLogin}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '12px 16px',
            backgroundColor: '#1877F2',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#166FE5';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = '#1877F2';
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          Continue with Facebook
        </button>
      )}
    </div>
  );
};

export default SocialLogin;